import type { CustomFieldType } from '../enums.js';
import { parseCsv } from '../schemas/import.js';
import { slugifyModuleName } from '../schemas/custom-module.js';
import { normalizeZohoName, type ZohoModuleName } from './fields.js';

/**
 * Reading a Zoho CRM *Data Backup* as it actually ships.
 *
 * A backup is two zips. `Data` holds one `<Module>_001.csv` per module, and
 * `Metadata` describes the org: its modules, fields, picklists, layouts and
 * roles. The data files are the same shape as a list-view export with three
 * differences that matter here:
 *
 *  - Every lookup column is suffixed `.id` and holds a Zoho Record Id.
 *  - A custom module's file carries a `_C` marker (`Content Shoot_C_001.csv`)
 *    and Zoho's own non-CRM modules (Services) have no marker at all.
 *  - Files-in-a-field (images, PDFs) get a sibling CSV of attachment rows,
 *    which describes files rather than records.
 *
 * Everything in this file is pure — strings in, plans out — so it is tested
 * without a database, and the dry run and the commit can share it.
 */

/** A module key: a built-in Zoho module, or `custom:<apiName>`. */
export type ZohoModuleKey = ZohoModuleName | `custom:${string}`;

export const ZOHO_CUSTOM_PREFIX = 'custom:';

export function customModuleKey(apiName: string): ZohoModuleKey {
  return `${ZOHO_CUSTOM_PREFIX}${apiName}`;
}

export function isCustomModuleKey(key: string): key is `custom:${string}` {
  return key.startsWith(ZOHO_CUSTOM_PREFIX);
}

// ---------------------------------------------------------------------------
// File names
// ---------------------------------------------------------------------------

/** `exports/Content Shoot_C_001.csv` → `Content Shoot_C_001`. */
export function fileStem(fileName: string): string {
  const base = fileName.split('/').pop() ?? fileName;
  return base.replace(/\.[^.]+$/u, '');
}

/** The module part of a stem: drop the `_001` page suffix and the `_C` marker. */
export function moduleLabelFromStem(stem: string): { label: string; marked: boolean } {
  const withoutPage = stem.replace(/_\d{3}$/u, '');
  const marked = /_C$/u.test(withoutPage);
  return { label: withoutPage.replace(/_C$/u, '').trim(), marked };
}

/**
 * Zoho modules that are not marked `_C` yet are not CRM's own either — the
 * Bookings-style Services module and its member join. A backup's metadata says
 * so (`Module Type`); this is the fallback for when only the Data zip is
 * uploaded.
 */
const UNMARKED_CUSTOM_STEMS = new Set(['services', 'services_x_users']);

/** Files that hold no records of their own and are not imported. */
const SKIPPED_STEMS: Record<string, string> = {
  emails: 'Email history is not imported.',
  forecasts: 'Forecasts are recomputed from deals.',
  visits: 'Website visit tracking is not imported.',
  invitees: 'Meeting invitees are not imported.',
  sticky_notes: 'Sticky notes are personal and not imported.',
  stickynotes: 'Sticky notes are personal and not imported.',
  stage_history: 'Deal stage history is rebuilt as deals move.',
  converted_lead_records: 'Lead conversions are carried on the lead itself.',
  attachments:
    'The attachment list is used by "Import attachments" (below), which also needs the attachments zip.',
  notes: 'Notes are imported with their parent records in a later release.',
  quoted_items: 'Line items are imported with their quote in a later release.',
  invoiced_items: 'Line items are imported with their invoice in a later release.',
  deal_contact_role: 'Deal contact roles are not imported.',
  contact_product_relation: 'Contact to product links are not imported.',
  lead_product_relation: 'Lead to product links are not imported.',
  campaigncontactmember: 'Campaign membership is not imported.',
  campaign_contact_member: 'Campaign membership is not imported.',
};

export type ZohoFileKind =
  { kind: 'custom'; label: string; apiName: string } | { kind: 'skip'; reason: string };

/**
 * Whether a file that is not a built-in module is a custom module, a child
 * table to skip, or unknown.
 *
 * `headers` is the first CSV row. An attachment sibling is recognised by its
 * header (`Id,Old Attachment Id,File Name,…`) rather than its name, because
 * its name is the field's label and could be anything.
 */
export function classifyBackupFile(
  fileName: string,
  headers: readonly string[],
  metadata?: ZohoBackupMetadata,
): ZohoFileKind | null {
  const stem = fileStem(fileName);
  const { label, marked } = moduleLabelFromStem(stem);
  const key = normalizeZohoName(label);

  if (headers[0] === 'Id' && headers.includes('Old Attachment Id')) {
    return {
      kind: 'skip',
      reason: 'File uploads are not imported (the files are not in the backup).',
    };
  }

  const skipped = SKIPPED_STEMS[key];
  if (skipped) return { kind: 'skip', reason: skipped };

  const fromMetadata = metadata?.customModules.find(
    (module) =>
      normalizeZohoName(module.label) === key || normalizeZohoName(module.apiName) === key,
  );
  const isCustom =
    Boolean(fromMetadata) ||
    (marked && headers[0] === 'Record Id') ||
    UNMARKED_CUSTOM_STEMS.has(key);
  if (!isCustom || headers[0] !== 'Record Id') return null;

  const displayLabel = fromMetadata?.label ?? label;
  return { kind: 'custom', label: displayLabel, apiName: slugifyModuleName(displayLabel) };
}

// ---------------------------------------------------------------------------
// Metadata zip
// ---------------------------------------------------------------------------

export interface ZohoMetaModule {
  id: string;
  label: string;
  apiName: string;
  singularLabel: string;
  /** Zoho's `Module Type` — custom modules are anything not "default". */
  type: string;
}

export interface ZohoMetaField {
  id: string;
  moduleId: string;
  label: string;
  apiName: string;
  dataType: string;
  isCustom: boolean;
  maxLength: number | null;
  /** Picklist choices, in Zoho's sort order. Empty for any other type. */
  options: string[];
}

export interface ZohoBackupMetadata {
  modules: ZohoMetaModule[];
  /** Modules Zoho flags as custom, which is where custom-module detection starts. */
  customModules: ZohoMetaModule[];
  fields: ZohoMetaField[];
  /** Roles and profiles, kept so the report can say what was seen. */
  roles: number;
  profiles: number;
  orgName: string | null;
}

export const EMPTY_ZOHO_METADATA: ZohoBackupMetadata = {
  modules: [],
  customModules: [],
  fields: [],
  roles: 0,
  profiles: 0,
  orgName: null,
};

/** Metadata CSV stems we read. Anything else in the Metadata zip is ignored. */
const METADATA_STEMS = [
  'modules',
  'fields',
  'picklistfieldproperties',
  'roles',
  'profiles',
  'company_details',
] as const;

/** Whether a file belongs to the Metadata zip rather than the Data zip. */
export function isMetadataFile(fileName: string): boolean {
  if (/(^|\/)metadata\//iu.test(fileName)) return true;
  if (/BackupDetails\.properties$/iu.test(fileName)) return true;
  const stem = normalizeZohoName(moduleLabelFromStem(fileStem(fileName)).label);
  return (
    METADATA_STEMS.includes(stem as (typeof METADATA_STEMS)[number]) ||
    [
      'layout',
      'layoutfieldproperties',
      'fielddependencyrelation',
      'currency',
      'currencyexchangeratehistory',
      'tags',
      'translation_language',
    ].includes(stem)
  );
}

function rowsOf(csv: string): Record<string, string>[] {
  const table = parseCsv(csv.replace(/^\uFEFF/u, ''));
  const [header, ...rows] = table;
  if (!header) return [];
  return rows
    .filter((row) => row.some((cell) => cell.trim() !== ''))
    .map((row) =>
      Object.fromEntries(header.map((name, index) => [name.trim(), (row[index] ?? '').trim()])),
    );
}

/** Builds the metadata view from whichever Metadata CSVs were uploaded. */
export function parseZohoMetadata(
  files: readonly { name: string; csv: string }[],
): ZohoBackupMetadata {
  const byStem = new Map<string, string>();
  for (const file of files) {
    byStem.set(normalizeZohoName(moduleLabelFromStem(fileStem(file.name)).label), file.csv);
  }

  const modules: ZohoMetaModule[] = rowsOf(byStem.get('modules') ?? '').map((row) => ({
    id: row['Module Id'] ?? '',
    label: row['Label'] || row['Name'] || '',
    apiName: row['Api Name'] || row['Name'] || '',
    singularLabel: row['Singular Label'] ?? '',
    type: row['Module Type'] ?? '',
  }));

  const picklists = new Map<string, { sort: number; value: string }[]>();
  for (const row of rowsOf(byStem.get('picklistfieldproperties') ?? '')) {
    const fieldId = row['Field Id'];
    const value = row['Value'] ?? row['Actual Value'];
    if (!fieldId || !value) continue;
    const list = picklists.get(fieldId) ?? [];
    if (!list.some((entry) => entry.value === value)) {
      list.push({ sort: Number(row['Sort Order']) || list.length, value });
    }
    picklists.set(fieldId, list);
  }

  const fields: ZohoMetaField[] = rowsOf(byStem.get('fields') ?? '').map((row) => ({
    id: row['Field Id'] ?? '',
    moduleId: row['Module Id'] ?? '',
    label: row['Label'] ?? '',
    apiName: row['Api Name'] ?? '',
    dataType: (row['Field Data Type'] ?? '').toLowerCase(),
    isCustom: /^true$/iu.test(row['Is Custom'] ?? ''),
    maxLength: Number(row['Maximum Length']) > 0 ? Number(row['Maximum Length']) : null,
    options: (picklists.get(row['Field Id'] ?? '') ?? [])
      .sort((left, right) => left.sort - right.sort)
      .map((entry) => entry.value),
  }));

  const company = rowsOf(byStem.get('company_details') ?? '')[0];

  return {
    modules,
    // Zoho's metadata marks built-in modules "default"; everything else is the
    // org's own (custom modules, and Zoho's add-on modules like Services).
    customModules: modules.filter((module) => module.type && !/^default$/iu.test(module.type)),
    fields,
    roles: rowsOf(byStem.get('roles') ?? '').length,
    profiles: rowsOf(byStem.get('profiles') ?? '').length,
    orgName: company?.['Org Name'] || null,
  };
}

// ---------------------------------------------------------------------------
// Custom-module plan
// ---------------------------------------------------------------------------

/**
 * Columns that are Zoho bookkeeping rather than data, per normalised name
 * (the `.id` suffix is stripped first). They describe the record's life in
 * Zoho — layouts, sync times, scoring, locks — and have no place on a
 * WHNUNIVERSE record. Everything else is data and is kept.
 */
const SYSTEM_COLUMNS = new Set([
  'record_id',
  'record_status',
  'modified_by',
  'created_by',
  'created_time',
  'modified_time',
  'layout',
  'currency',
  'exchange_rate',
  'last_activity_time',
  'user_modified_time',
  'system_modified_time',
  'user_related_activity_time',
  'system_related_activity_time',
  'change_log_time',
  'locked',
  'last_enriched_time',
  'enrich_status',
  'record_approval_status',
  'is_record_duplicate',
  'unsubscribed_mode',
  'unsubscribed_time',
  'email_opt_out',
  'secondary_email',
  'score',
  'positive_score',
  'negative_score',
  'touch_point_score',
  'positive_touch_point_score',
  'negative_touch_point_score',
  'record_creation_source_id',
  'connected_to',
  'connected_to.module',
]);

/** Whether a header is Zoho bookkeeping (and, for standard modules, not worth keeping). */
export function isZohoSystemColumn(header: string): boolean {
  const key = normalizeZohoName(header).replace(/\.id$/u, '');
  return SYSTEM_COLUMNS.has(key) || /_image$/u.test(key);
}

export interface CustomFieldPlan {
  /** The CSV header this field is read from. */
  header: string;
  apiName: string;
  label: string;
  type: CustomFieldType;
  options: { value: string; label: string }[];
  maxLength: number | null;
  /** For a LOOKUP: a built-in module name (`ACCOUNT`) or `custom:<apiName>`. */
  lookupTarget: string | null;
}

export interface CustomModulePlan {
  apiName: string;
  label: string;
  pluralLabel: string;
  /** The header whose value titles a record. */
  nameHeader: string | null;
  /** The `<Module> Owner.id` header, resolved to the business manager. */
  ownerHeader: string | null;
  idHeader: string | null;
  fields: CustomFieldPlan[];
}

/** Lookups whose header names a built-in module. */
const STANDARD_LOOKUP_HINTS: [RegExp, string][] = [
  [/^(account|associated_account|account_associated|customer|company|client)(_name)?$/u, 'ACCOUNT'],
  [/^(contact)(_name)?$/u, 'CONTACT'],
  [/^(deal|potential)(_name)?$/u, 'DEAL'],
  [/^(lead)(_name)?$/u, 'LEAD'],
  [/^(product)(_name)?$/u, 'PRODUCT'],
  [/^(vendor)(_name)?$/u, 'VENDOR'],
  [/^(campaign)(_name)?$/u, 'CAMPAIGN'],
  [/^(case)(_name)?$/u, 'CASE'],
];

/** camelCase JSONB key from a header: `Event Date & Time` → `eventDateTime`. */
export function fieldApiNameFor(header: string, taken: ReadonlySet<string>): string {
  const words = header
    .replace(/\.id$/iu, '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/gu, '')
    .split(/[^A-Za-z0-9]+/u)
    .filter(Boolean);
  const camel = words
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word[0]!.toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join('');
  let base = /^[a-z]/u.test(camel) ? camel : `f${camel[0]?.toUpperCase() ?? ''}${camel.slice(1)}`;
  if (base.length < 2) base = `${base || 'field'}Value`;
  base = base.slice(0, 44);

  let candidate = base;
  for (let suffix = 2; taken.has(candidate) || RESERVED.has(candidate); suffix += 1) {
    candidate = `${base}${suffix}`;
  }
  return candidate;
}

const RESERVED = new Set([
  'id',
  'organizationId',
  'businessManagerId',
  'createdAt',
  'updatedAt',
  'deletedAt',
  'customFields',
]);

/** Zoho `Field Data Type` → our type. Unknown types fall to TEXT, never to an error. */
export function typeFromZoho(dataType: string): CustomFieldType | null {
  switch (dataType) {
    case 'text':
    case 'string':
    case 'autonumber':
    case 'formula':
      return 'TEXT';
    case 'textarea':
    case 'rich_text':
      return 'TEXTAREA';
    case 'integer':
    case 'bigint':
    case 'double':
    case 'percent':
      return 'NUMBER';
    case 'currency':
      return 'CURRENCY';
    case 'boolean':
      return 'BOOLEAN';
    case 'date':
      return 'DATE';
    case 'datetime':
      return 'DATETIME';
    case 'email':
      return 'EMAIL';
    case 'phone':
      return 'PHONE';
    case 'website':
    case 'url':
      return 'URL';
    case 'picklist':
      return 'PICKLIST';
    case 'multiselectpicklist':
      return 'MULTI_PICKLIST';
    case 'lookup':
    case 'userlookup':
    case 'ownerlookup':
      return 'LOOKUP';
    default:
      return null;
  }
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/u;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/u;
const NUMERIC = /^-?\d[\d,]*(\.\d+)?$/u;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const URLISH = /^https?:\/\/\S+$/iu;

/**
 * A type from the cells themselves, when metadata is absent or silent.
 *
 * Conservative on purpose: a column is typed only if *every* non-blank cell
 * fits, and the fallback is TEXT. A TEXT field accepts anything, so being
 * wrong in this direction loses nothing; a NUMBER field that meets "N/A" in
 * row 900 would reject the row.
 */
export function inferTypeFromValues(values: readonly string[], header: string): CustomFieldType {
  const filled = values.map((value) => value.trim()).filter(Boolean);

  if (filled.length === 0) {
    return /(description|remark|requirement|transcript|details|comment|notes?)$/iu.test(header)
      ? 'TEXTAREA'
      : 'TEXT';
  }
  if (filled.every((value) => /^(true|false)$/iu.test(value))) return 'BOOLEAN';
  if (filled.every((value) => ISO_DATE.test(value))) return 'DATE';
  if (filled.every((value) => ISO_DATETIME.test(value))) return 'DATETIME';
  if (filled.every((value) => EMAIL.test(value))) return 'EMAIL';
  if (filled.every((value) => URLISH.test(value))) return 'URL';
  // Leading zeros mean an identifier (a phone, a GST number), not a quantity.
  if (filled.every((value) => NUMERIC.test(value) && !/^-?0\d/u.test(value))) return 'NUMBER';
  if (filled.some((value) => value.length > 255 || value.includes('\n'))) return 'TEXTAREA';
  return 'TEXT';
}

function pluralize(label: string): string {
  if (/s$/iu.test(label)) return label;
  if (/[^aeiou]y$/iu.test(label)) return `${label.slice(0, -1)}ies`;
  return `${label}s`;
}

/** `Service Name.id` against custom labels `Services`, `Services X Users`. */
function matchCustomTarget(
  base: string,
  customLabels: readonly { label: string; apiName: string }[],
): string | null {
  const wanted = normalizeZohoName(base)
    .replace(/_name$/u, '')
    .replace(/s$/u, '');
  const hit = customLabels.find(
    (entry) => normalizeZohoName(entry.label).replace(/s$/u, '') === wanted,
  );
  return hit ? `custom:${hit.apiName}` : null;
}

/**
 * Plans one custom module from its CSV header (and, if there, metadata).
 *
 * Fields come from the header, in file order. Metadata, when it knows the
 * field, decides the type and the picklist choices; otherwise the cells decide.
 * Lookup columns point at the built-in module their name suggests, or at a
 * sibling custom module; one that matches neither is kept as text, so the
 * Zoho Record Id survives and nothing is lost.
 */
export function planCustomModule(input: {
  label: string;
  apiName: string;
  headers: readonly string[];
  rows: readonly string[][];
  metadata?: ZohoBackupMetadata;
  otherCustomModules?: readonly { label: string; apiName: string }[];
}): CustomModulePlan {
  const { headers, rows } = input;
  const meta = input.metadata ?? EMPTY_ZOHO_METADATA;
  const metaModule = meta.modules.find(
    (module) =>
      normalizeZohoName(module.label) === normalizeZohoName(input.label) ||
      normalizeZohoName(module.apiName) === normalizeZohoName(input.label),
  );
  const metaFields = metaModule
    ? meta.fields.filter((field) => field.moduleId === metaModule.id)
    : [];
  const metaFor = (header: string): ZohoMetaField | undefined => {
    const wanted = normalizeZohoName(header.replace(/\.id$/iu, ''));
    return metaFields.find(
      (field) =>
        normalizeZohoName(field.label) === wanted || normalizeZohoName(field.apiName) === wanted,
    );
  };

  const idHeader = headers.find((header) => normalizeZohoName(header) === 'record_id') ?? null;
  const ownerHeader = headers.find((header) => /(^|\s)owner\.id$/iu.test(header)) ?? null;

  // The record title: Zoho's Name field is the first data column after the
  // bookkeeping, and is labelled `<Module> Name` more often than not.
  const dataHeaders = headers.filter(
    (header) => header !== ownerHeader && !isZohoSystemColumn(header),
  );
  const nameHeader =
    dataHeaders.find(
      (header) => normalizeZohoName(header) === normalizeZohoName(`${input.label} name`),
    ) ??
    dataHeaders.find((header) => !/\.id$/iu.test(header)) ??
    null;

  const taken = new Set<string>();
  const fields: CustomFieldPlan[] = [];

  for (const header of dataHeaders) {
    const column = headers.indexOf(header);
    const values = rows.map((row) => row[column] ?? '');
    const known = metaFor(header);
    const isLookupHeader = /\.id$/iu.test(header);
    const base = header.replace(/\.id$/iu, '');

    let type: CustomFieldType;
    let lookupTarget: string | null = null;
    let options: { value: string; label: string }[] = [];

    if (isLookupHeader) {
      const key = normalizeZohoName(base);
      const standard = STANDARD_LOOKUP_HINTS.find(([pattern]) => pattern.test(key))?.[1] ?? null;
      const custom = standard ? null : matchCustomTarget(base, input.otherCustomModules ?? []);
      lookupTarget = standard ?? custom;
      // No recognisable target: keep the Zoho Record Id as text so it is not lost.
      type = lookupTarget ? 'LOOKUP' : 'TEXT';
    } else {
      const fromMeta = known ? typeFromZoho(known.dataType) : null;
      type = fromMeta ?? inferTypeFromValues(values, header);
      // A metadata LOOKUP on a non-`.id` column has nowhere to point.
      if (type === 'LOOKUP') type = 'TEXT';
      if ((type === 'PICKLIST' || type === 'MULTI_PICKLIST') && known && known.options.length > 0) {
        options = known.options.map((value) => ({
          value: value.slice(0, 80),
          label: value.slice(0, 80),
        }));
      } else if (type === 'PICKLIST' || type === 'MULTI_PICKLIST') {
        // A picklist with no known choices cannot be created; text keeps the values.
        type = 'TEXT';
      }
    }

    const apiName = fieldApiNameFor(base || header, taken);
    taken.add(apiName);
    fields.push({
      header,
      apiName,
      label: (known?.label || base || header).slice(0, 80),
      type,
      options,
      maxLength: known?.maxLength && type === 'TEXT' ? Math.min(known.maxLength, 20_000) : null,
      lookupTarget,
    });
  }

  return {
    apiName: input.apiName,
    label: input.label,
    pluralLabel: pluralize(input.label),
    nameHeader,
    ownerHeader,
    idHeader,
    fields,
  };
}
